import React, { useState, useEffect } from 'react';
import { View, Modal, ActivityIndicator } from 'react-native';
import { translate } from '../../../services/translate';
import FormActions from '../../../components/FormActions';
import MyAppText from '../../../components/MyAppText';
import { cameraService } from '../../../services/central-api/cameras';
import { WindowInformation } from '../../../services/window-information';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import FilterDropdown from '../../../components/Filter/components/FilterDropdown';

interface ModalProps {
    isModalVisible: boolean;
    onClose: () => void;
    mosaic?: Mosaic;
}

export default function MosaicShareModal({ onClose, mosaic, isModalVisible }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
    const [selectableUsers, setSelectableUsers] = useState<{ label: string; value: number; }[]>([]);

    function cleanValues() {
        setSelectedUserIds([]);
        setSelectableUsers([]);
    }

    async function loadPageInfo() {
        try {
            setIsLoading(true);

            const users = await cameraService.getAvailableUsersToShareMosaic();

            setSelectableUsers(users.map(user => ({
                label: `${user.warName ? user.warName : user.name} - RF: ${user.registry}`,
                value: user.id
            })));
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (isModalVisible) {
            loadPageInfo();
        }
    }, [isModalVisible]);

    async function formSubmit() {
        if (!mosaic) {
            return;
        }

        try {
            await cameraService.shareMosaic(mosaic?.id, selectedUserIds);

            cleanValues();
            onClose();

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function isFormValid(): boolean {
        return selectedUserIds.length > 0;
    }

    return (
        <Modal
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                cleanValues();
                onClose();
            }}
            transparent={true}
        >
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : {}]}>
                    {isLoading ?
                        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', rowGap: 10 }}>
                            <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                            <MyAppText style={{ fontSize: 16, color: theme.colors.primaryButton.background }}>{translate('loading')}...</MyAppText>
                        </View>
                        :
                        <>
                            <View style={{ flex: 1, rowGap: 15 }}>
                                <FilterDropdown
                                    label={translate('share_with') + ':'}
                                    items={selectableUsers}
                                    multiple={true}
                                    mode="BADGE"
                                    setValue={setSelectedUserIds}
                                    value={selectedUserIds}
                                    labelFontSize={14}
                                />
                            </View>
                            <FormActions
                                onSubmit={formSubmit}
                                onClose={() => {
                                    cleanValues();
                                    onClose();
                                }}
                                disabled={!isFormValid()} />
                        </>
                    }
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 300,
        maxWidth: 850,
        maxHeight: 650,
        backgroundColor: theme.colors.card.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 15
    },
}));
